import type { StateCreator } from 'zustand/vanilla';
import type { ClawPassType, ClawStatus, ClawV1Claw, ClawV1FileItem, ClawV1Session } from '@/app/types';

type ClawYoloModelChoice = 'auto' | 'q2' | 'q3';
type ClawBillingMode = 'credits' | 'claw_pass';

export interface PendingClawInput {
  prompt: string;
  referenceImageUrl?: string;
}

interface ClawStoreState {
  clawStatus: ClawStatus | null;
  activeClaw: ClawV1Claw | null;
  clawPass: ClawPassType | null;
  isClawInfoInitialized: boolean;
  activeSessionId: string | null;
  clawSidebarCollapsed: boolean;
  sessions: ClawV1Session[];
  sessionsOwnerClaw: ClawV1Claw | null;
  clawRightPanelOpen: boolean;
  clawFileListRefreshKey: number;
  // 右侧文件面板正在拉取目录中：用于在 ChatHeader 等入口给 toggle 按钮上 loading
  clawFolderLoading: boolean;
  // 右侧文件面板的当前目录与缓存内容在同一个 claw 下跨 session 共享，
  // 切换 chat 时保持文件浏览状态不变。
  clawFolderPath: string;
  clawCurrentFolderItems: Record<string, ClawV1FileItem[]>;
  // 极速模式 / md story：session 与媒体目录的绑定，切换 session 时用于自动定位
  sessionMediaFolderMap: Record<string, string>;
  // 每个 yolo session 独立记忆模型选择；欢迎页 (!activeSessionId) 不写入这里。
  yoloModelChoiceBySession: Record<string, ClawYoloModelChoice>;
  // 欢迎页发送时暂存的选择，待 activeSessionId 出现后继承到 yoloModelChoiceBySession；
  // 提升到 store 层是为了跨 WelcomeChatPanel → ConversationChatPanel 的 unmount/remount 持久。
  pendingYoloModelChoiceForNextSession: ClawYoloModelChoice | null;
  // 每个 session 独立记忆扣费模式（积分 / 算力）；欢迎页 (!activeSessionId) 不写入这里。
  billingModeBySession: Record<string, ClawBillingMode>;
  // 欢迎页发送时暂存的扣费模式，待 activeSessionId 出现后继承到 billingModeBySession；
  // 与 yolo 同因：跨 WelcomeChatPanel → ConversationChatPanel unmount/remount 持久，避免重挂后被默认值覆盖。
  pendingBillingModeForNextSession: ClawBillingMode | null;
  // 从首页 HeroSection 跳转到 /claw 时暂存的输入（prompt + 可选参考图），
  // claw Welcome 页 mount 后消费并清空。
  pendingClawInput: PendingClawInput | null;
}

export interface ClawStore extends ClawStoreState {
  setClawStatus: (status: ClawStatus | null) => void;
  setActiveClaw: (claw: ClawV1Claw | null) => void;
  setClawPass: (clawPass: ClawPassType | null) => void;
  setClawInfoInitialized: (initialized: boolean) => void;
  setActiveSessionId: (sessionId: string | null) => void;
  toggleClawSidebar: (collapsed?: boolean) => void;
  setSessions: (claw: ClawV1Claw, sessions: ClawV1Session[]) => void;
  patchSession: (sessionId: string, patch: Partial<ClawV1Session>) => void;
  clearSessionUnread: (sessionId: string) => void;
  prependSession: (session: ClawV1Session) => void;
  removeSession: (sessionId: string) => void;
  clearSessions: () => void;
  setClawRightPanelOpen: (open: boolean) => void;
  toggleClawRightPanelOpen: () => void;
  bumpClawFileListRefreshKey: () => void;
  setClawFolderLoading: (loading: boolean) => void;
  setClawFolderPath: (path: string) => void;
  setClawCurrentFolderItems: (folderPath: string, items: ClawV1FileItem[]) => void;
  setSessionMediaFolder: (sessionId: string, folderPath: string) => void;
  removeSessionMediaFolder: (sessionId: string) => void;
  setYoloModelChoiceForSession: (sessionId: string, choice: ClawYoloModelChoice) => void;
  setPendingYoloModelChoiceForNextSession: (choice: ClawYoloModelChoice | null) => void;
  setBillingModeForSession: (sessionId: string, billingMode: ClawBillingMode) => void;
  setPendingBillingModeForNextSession: (billingMode: ClawBillingMode | null) => void;
  setPendingClawInput: (input: PendingClawInput | null) => void;
}

export const createClawStore: StateCreator<ClawStore, [], [], ClawStore> = (set) => ({
  clawStatus: null,
  activeClaw: null,
  clawPass: null,
  isClawInfoInitialized: false,
  activeSessionId: null,
  clawSidebarCollapsed: false,
  sessions: [],
  sessionsOwnerClaw: null,
  clawRightPanelOpen: false,
  clawFileListRefreshKey: 0,
  clawFolderLoading: false,
  clawFolderPath: 'media',
  clawCurrentFolderItems: {},
  sessionMediaFolderMap: {},
  yoloModelChoiceBySession: {},
  pendingYoloModelChoiceForNextSession: null,
  billingModeBySession: {},
  pendingBillingModeForNextSession: null,
  pendingClawInput: null,
  setClawStatus: (status) => set({ clawStatus: status }),
  setActiveClaw: (claw) => set({ activeClaw: claw }),
  setClawPass: (clawPass) => set({ clawPass }),
  setClawInfoInitialized: (initialized) => set({ isClawInfoInitialized: initialized }),
  setActiveSessionId: (sessionId) => set({ activeSessionId: sessionId }),
  toggleClawSidebar: (collapsed) =>
    set((state) => ({
      clawSidebarCollapsed: typeof collapsed === 'boolean' ? collapsed : !state.clawSidebarCollapsed,
    })),
  setSessions: (claw, sessions) =>
    set({
      sessionsOwnerClaw: claw,
      sessions,
    }),
  patchSession: (sessionId, patch) =>
    set((state) => {
      const idx = state.sessions.findIndex((item) => item.id === sessionId);
      if (idx === -1) return state;
      const sessions = [...state.sessions];
      sessions[idx] = { ...sessions[idx], ...patch };
      return { sessions };
    }),
  clearSessionUnread: (sessionId) =>
    set((state) => {
      const idx = state.sessions.findIndex((item) => item.id === sessionId);
      if (idx === -1 || (state.sessions[idx].unread_count ?? 0) === 0) return state;
      const sessions = [...state.sessions];
      sessions[idx] = { ...sessions[idx], unread_count: 0 };
      return { sessions };
    }),
  prependSession: (session) =>
    set((state) => ({
      sessionsOwnerClaw: state.activeClaw,
      sessions: [session, ...state.sessions.filter((item) => item.id !== session.id)],
    })),
  // 仅前端回滚：用于 createSession 成功但后续素材全部上传失败的场景，
  // 让 UI 不再显示这个孤儿 session，后端历史记录交给生命周期清理。
  removeSession: (sessionId) =>
    set((state) => {
      const nextSessions = state.sessions.filter((item) => item.id !== sessionId);
      const nextMediaFolderMap = { ...state.sessionMediaFolderMap };
      delete nextMediaFolderMap[sessionId];
      const nextYoloModelChoiceBySession = { ...state.yoloModelChoiceBySession };
      delete nextYoloModelChoiceBySession[sessionId];
      const nextBillingModeBySession = { ...state.billingModeBySession };
      delete nextBillingModeBySession[sessionId];
      return {
        sessions: nextSessions,
        sessionMediaFolderMap: nextMediaFolderMap,
        yoloModelChoiceBySession: nextYoloModelChoiceBySession,
        billingModeBySession: nextBillingModeBySession,
        activeSessionId: state.activeSessionId === sessionId ? null : state.activeSessionId,
      };
    }),
  clearSessions: () =>
    set({
      activeSessionId: null,
      sessions: [],
      sessionsOwnerClaw: null,
      clawFolderPath: 'media',
      clawCurrentFolderItems: {},
      sessionMediaFolderMap: {},
      yoloModelChoiceBySession: {},
      pendingYoloModelChoiceForNextSession: null,
      billingModeBySession: {},
      pendingBillingModeForNextSession: null,
    }),
  setClawRightPanelOpen: (open) => set({ clawRightPanelOpen: open }),
  toggleClawRightPanelOpen: () => set((state) => ({ clawRightPanelOpen: !state.clawRightPanelOpen })),
  bumpClawFileListRefreshKey: () =>
    set((state) => ({
      clawFileListRefreshKey: state.clawFileListRefreshKey + 1,
    })),
  setClawFolderLoading: (loading) => set({ clawFolderLoading: loading }),
  setClawFolderPath: (path) => set({ clawFolderPath: path }),
  setClawCurrentFolderItems: (folderPath, items) =>
    set((state) => ({
      clawCurrentFolderItems: {
        ...state.clawCurrentFolderItems,
        [folderPath]: items,
      },
    })),

  setSessionMediaFolder: (sessionId, folderPath) =>
    set((state) => ({
      sessionMediaFolderMap: {
        ...state.sessionMediaFolderMap,
        [sessionId]: folderPath,
      },
    })),

  removeSessionMediaFolder: (sessionId) =>
    set((state) => {
      if (!(sessionId in state.sessionMediaFolderMap)) return state;
      const next = { ...state.sessionMediaFolderMap };
      delete next[sessionId];
      return { sessionMediaFolderMap: next };
    }),

  setYoloModelChoiceForSession: (sessionId, choice) =>
    set((state) => {
      if (!sessionId) return state;
      if (state.yoloModelChoiceBySession[sessionId] === choice) return state;
      return {
        yoloModelChoiceBySession: {
          ...state.yoloModelChoiceBySession,
          [sessionId]: choice,
        },
      };
    }),

  setPendingYoloModelChoiceForNextSession: (choice) => set({ pendingYoloModelChoiceForNextSession: choice }),

  setBillingModeForSession: (sessionId, billingMode) =>
    set((state) => {
      if (!sessionId) return state;
      if (state.billingModeBySession[sessionId] === billingMode) return state;
      return {
        billingModeBySession: {
          ...state.billingModeBySession,
          [sessionId]: billingMode,
        },
      };
    }),

  setPendingBillingModeForNextSession: (billingMode) => set({ pendingBillingModeForNextSession: billingMode }),

  setPendingClawInput: (input) => set({ pendingClawInput: input }),
});
